import React, { useCallback, useState } from 'react';
import { AccessibilityProvider } from './components/UI/AccessibilityProvider';
import { TrackingScripts } from './components/Tracking/TrackingScripts';
import { SEOHead } from './components/SEO/SEOHead';
import CookieConsentBanner, {
  analyticsAllowed,
} from './components/Cookies/CookieConsent';
import LandingPage from './landing-redesign/LandingPage';
import TrialPage from './landing-redesign/TrialPage';
import LegalPage from './legal/LegalPage';
import { legalDocumentForPath } from './legal/legalDocuments';

const BRAND = 'TuMesaYa';

/**
 * Origen público de la landing. Desde el issue #293 la web de marca cuelga de
 * centraldereservas: tumesaya.com sigue siendo nuestro pero sólo responde un
 * 301 hacia aquí.
 */
const ORIGIN = 'https://tumesaya.centraldereservas.ai';

/**
 * Enrutado del sitio público. No hay router: el sitio son cuatro tipos de
 * página y nginx ya sirve index.html para cualquier ruta que no sea de la app
 * (login, alta y panel siguen viniendo de Nuxt — ver deploy/nginx en el repo
 * reserva-ya).
 */
const App: React.FC = () => {
  const { pathname } = window.location;
  const isTrialPage = pathname.startsWith('/prueba');
  const legalDoc = legalDocumentForPath(pathname);

  // Las herramientas de medición solo se montan tras el consentimiento.
  const [analytics, setAnalytics] = useState(analyticsAllowed);
  const handleConsentChange = useCallback(
    (allowed: boolean) => setAnalytics(allowed),
    []
  );

  if (legalDoc) {
    return (
      <AccessibilityProvider>
        <SEOHead
          title={`${legalDoc.title} | ${BRAND}`}
          description={`${legalDoc.title} de ${BRAND}. Información legal, privacidad y condiciones del servicio.`}
          image={`${ORIGIN}/og.png`}
          canonical={`${ORIGIN}/${legalDoc.slug}`}
        />
        <LegalPage document={legalDoc} brandName={BRAND} />
        <CookieConsentBanner onConsentChange={handleConsentChange} />
        {analytics && <TrackingScripts />}
      </AccessibilityProvider>
    );
  }

  return (
    <AccessibilityProvider>
      <SEOHead
        title={
          isTrialPage
            ? `Activa tu prueba de 30 minutos | ${BRAND}`
            : `Reservas por teléfono 24/7 · WhatsApp próximamente | ${BRAND}`
        }
        description={
          isTrialPage
            ? `Activa tu prueba gratuita de 30 minutos de ${BRAND} y descubre cómo gestionar reservas por teléfono con inteligencia artificial.`
            : `${BRAND} gestiona reservas por teléfono, envía recordatorios y procesa cancelaciones las 24 horas. El canal de WhatsApp llegará próximamente.`
        }
        image={`${ORIGIN}/og.png`}
        canonical={isTrialPage ? `${ORIGIN}/prueba` : ORIGIN}
        robots={isTrialPage ? 'noindex,follow' : undefined}
      />
      {analytics && <TrackingScripts />}
      {isTrialPage ? <TrialPage /> : <LandingPage />}
      <CookieConsentBanner onConsentChange={handleConsentChange} />
    </AccessibilityProvider>
  );
};

export default App;
